import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { useConfirm } from '../context/ConfirmContext';
import './FortnoxInvoiceSection.css';

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10)
  };
};

const FortnoxInvoiceSection = ({ disabled = false }) => {
  const { t } = useTranslation(['company', 'common']);
  const confirm = useConfirm();
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [{ startDate, endDate }, setDateRange] = useState(defaultDateRange);
  const [preview, setPreview] = useState(null);
  const [lineQuantities, setLineQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [pushSummary, setPushSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await axios.get('/api/locations');
        setLocations(response.data?.data || []);
      } catch (err) {
        setError(formatError(err) || t('fortnoxInvoiceLocationsFailed'));
      }
    };
    loadLocations();
  }, [t]);

  const buildPayload = useCallback(() => ({
    locationId: Number(locationId),
    startDate,
    endDate,
    lineQuantities
  }), [locationId, startDate, endDate, lineQuantities]);

  const handlePreview = async () => {
    if (!locationId) {
      setError(t('fortnoxInvoiceLocationRequired'));
      return;
    }
    setLoading(true);
    setError('');
    setPushSummary(null);
    try {
      const response = await axios.post('/api/integrations/fortnox/invoices/preview', buildPayload());
      const data = response.data?.data || null;
      setPreview(data);
      const qtyMap = {};
      (data?.lines || []).forEach((line) => {
        qtyMap[line.articleNumber] = line.quantity;
      });
      setLineQuantities(qtyMap);
    } catch (err) {
      setPreview(null);
      setError(formatError(err) || t('fortnoxInvoicePreviewFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePush = async () => {
    if (!preview?.invoiceReady) {
      setError(t('fortnoxInvoiceNotReady'));
      return;
    }
    if (!(await confirm(t('fortnoxInvoiceConfirmPush'), { danger: false }))) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/integrations/fortnox/invoices', buildPayload());
      setPushSummary(response.data?.data || null);
      setPreview(null);
    } catch (err) {
      setError(formatError(err) || t('fortnoxInvoicePushFailed'));
    } finally {
      setLoading(false);
    }
  };

  const updateLineQty = (articleNumber, value) => {
    setLineQuantities((prev) => ({ ...prev, [articleNumber]: value }));
  };

  const readyLocations = locations.filter((loc) => loc.fortnoxBillingStatus === 'ready');

  return (
    <div className="fortnox-invoice-block">
      <h4>{t('fortnoxInvoiceTitle')}</h4>
      <p className="section-description">{t('fortnoxInvoiceDescription')}</p>

      {error && <div className="error">{error}</div>}

      <div className="fortnox-invoice-form">
        <div className="form-group">
          <label htmlFor="fortnox-invoice-location">{t('fortnoxInvoiceLocationLabel')}</label>
          <select
            id="fortnox-invoice-location"
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              setPreview(null);
              setPushSummary(null);
            }}
            disabled={disabled || loading}
          >
            <option value="">{t('fortnoxInvoiceLocationPlaceholder')}</option>
            {readyLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>
          {locations.length > 0 && readyLocations.length === 0 && (
            <p className="fortnox-invoice-hint">{t('fortnoxInvoiceNoReadyLocations')}</p>
          )}
        </div>

        <div className="fortnox-invoice-dates">
          <div className="form-group">
            <label htmlFor="fortnox-invoice-start">{t('fortnoxInvoiceStartDate')}</label>
            <input
              id="fortnox-invoice-start"
              type="date"
              value={startDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
              disabled={disabled || loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fortnox-invoice-end">{t('fortnoxInvoiceEndDate')}</label>
            <input
              id="fortnox-invoice-end"
              type="date"
              value={endDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
              disabled={disabled || loading}
            />
          </div>
        </div>

        <div className="endpoint-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePreview}
            disabled={disabled || loading || !locationId}
          >
            {t('fortnoxInvoicePreview')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePush}
            disabled={disabled || loading || !preview?.invoiceReady}
          >
            {t('fortnoxInvoicePush')}
          </button>
        </div>
      </div>

      {preview?.warnings?.length > 0 && (
        <ul className="fortnox-invoice-warnings">
          {preview.warnings.map((warning, index) => (
            <li key={`${warning.type}-${warning.taskId || index}`}>{warning.message}</li>
          ))}
        </ul>
      )}

      {preview && (
        <div className="fortnox-invoice-preview">
          <p>
            <strong>{t('fortnoxInvoiceCustomer')}:</strong>
            {' '}
            {preview.customerNumber}
            {preview.customerName ? ` — ${preview.customerName}` : ''}
          </p>
          <p>
            <strong>{t('fortnoxInvoiceDates')}:</strong>
            {' '}
            {preview.invoiceDate}
            {' → '}
            {preview.dueDate}
          </p>
          {preview.lines.length === 0 ? (
            <p>{t('fortnoxInvoiceNoLines')}</p>
          ) : (
            <div className="fortnox-maps-table-wrap">
              <table className="fortnox-maps-table">
                <thead>
                  <tr>
                    <th>{t('fortnoxArticleNumber')}</th>
                    <th>{t('fortnoxArticleDescription')}</th>
                    <th>{t('fortnoxInvoiceQty')}</th>
                    <th>{t('fortnoxArticlePrice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line) => (
                    <tr key={line.articleNumber}>
                      <td>{line.articleNumber}</td>
                      <td>{line.description}</td>
                      <td>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={lineQuantities[line.articleNumber] ?? line.quantity}
                          onChange={(e) => updateLineQty(line.articleNumber, e.target.value)}
                          disabled={disabled || loading}
                          aria-label={t('fortnoxInvoiceQty')}
                        />
                      </td>
                      <td>{line.unitPrice != null ? line.unitPrice : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.subtotalEstimate != null && (
            <p className="fortnox-invoice-subtotal">
              {t('fortnoxInvoiceSubtotal', { amount: preview.subtotalEstimate.toFixed(2) })}
            </p>
          )}
          <p className="fortnox-invoice-task-count">
            {t('fortnoxInvoiceTaskCount', { count: preview.taskIds?.length || 0 })}
          </p>
        </div>
      )}

      {pushSummary && (
        <div className="success fortnox-invoice-push-summary" role="status">
          <p>
            {t('fortnoxInvoicePushSuccess', {
              doc: pushSummary.fortnoxDocumentNumber || '—',
              invoice: pushSummary.fortnoxInvoiceNumber || '—'
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default FortnoxInvoiceSection;
